import { isFeatureEnabled } from '@atlassiansox/frontend-feature-encoding';
import {
	isTailoredOnboardingM2P5TailoredTeamType,
	isTailoredOnboardingTeamType,
} from '@jira/growth__tailored-onboarding/src/isTailoredOnboardingTeamType.tsx';
import { ROOT_TASK } from '../../common/constants.tsx';
import type { Cards, TaskListItem, TaskList } from '../../common/types.tsx';
import { USER_ROLE } from '../model/constants.tsx';
import type { ProjectState } from '../model/types.tsx';
import { JSM } from './config/jsm/index.tsx';
import { JWM } from './config/jwm/index.tsx';
import { TAILORED_ONBOARDING_BUSINESS_CONFIG } from './config/jwm/tailored-onboarding.tsx';
import { PLAN } from './config/plan/index.tsx';
import {
	type Experiment,
	Experience,
	Feature,
	Permissions,
	type TeamType,
} from './config/tailored-onboarding/constants.tsx';
import {
	TAILORED_ONBOARDING_BUSINESS,
	TAILORED_ONBOARDING_WITH_SINGLE_CONFIG,
} from './config/tailored-onboarding/index.tsx';
import type { GetTasksProps } from './GetTasksProps.tsx';
import { getTasks } from './manager/getTasks.tsx';
import { isJwmProject, isJsmProject } from './utils.tsx';

const getTaskListItem = ({
	variants,
	taskVariants,
	taskList,
	taskGroupParent,
}: {
	variants: string[];
	taskVariants: Cards;
	taskList: TaskList;
	taskGroupParent: string;
}): TaskListItem => {
	// Removed jira_rt_quickstart_m1 feature flag - serving true case (no reverse trial task list)
	const { title, items } = taskList[taskGroupParent] ?? taskList[ROOT_TASK];

	return {
		title,
		items: getTasks(variants, taskVariants, items),
	};
};

const createJwmTasks = ({
	projectState,
	taskGroupParent,
	taskList,
	teamType,
}: {
	projectState: ProjectState | null;
	taskGroupParent: string;
	taskList?: TaskList;
	teamType?: string;
	isEligibleForTailoredOnboardingM2P5?: boolean;
}): TaskListItem => {
	return createTailoredOnboardingBusinessTasks({
		taskGroupParent,
		teamType,
		projectState,
		taskList,
	});
};

const createJsmTasks = (
	projectState: ProjectState | null,
	taskGroupParent: string,
): TaskListItem => {
	const variants: string[] = [];

	variants.push(JSM.experiments.projectTheming);

	if (projectState?.isProjectAdmin) {
		variants.push(JSM.userRole.projectAdmin);
	}

	if (projectState?.userRole === USER_ROLE.JIRA_ADMIN) {
		variants.push(JSM.userRole.jiraAdmin);
	}

	if (projectState?.userRole === USER_ROLE.SITE_ADMIN) {
		variants.push(JSM.userRole.siteAdmin);
	}

	return getTaskListItem({
		variants,
		taskVariants: JSM.taskVariants,
		taskList: JSM.taskList,
		taskGroupParent,
	});
};

type TailoredOnboardingVariants =
	| Experiment
	| (typeof TeamType)[keyof typeof TeamType]
	| (typeof Feature)[keyof typeof Feature]
	| (typeof Permissions)[keyof typeof Permissions]
	| (typeof Experience)[keyof typeof Experience]
	| (typeof JWM.experiments)[keyof typeof JWM.experiments]
	| (typeof JWM.userRole)[keyof typeof JWM.userRole];

const createPlanTasks = (
	taskGroupParent: string,
	features?: {
		hasPlanDependencies?: boolean;
		hasPlanProgram?: boolean;
		hasPlanReleases?: boolean;
		hasPlanTeams?: boolean;
	},
): TaskListItem => {
	const variants: string[] = [];

	if (features?.hasPlanDependencies) {
		variants.push(Feature.hasPlanDependencies);
	}

	if (features?.hasPlanProgram) {
		variants.push(Feature.hasPlanProgram);
	}

	if (features?.hasPlanReleases) {
		variants.push(Feature.hasPlanReleases);
	}

	if (features?.hasPlanTeams) {
		variants.push(Feature.hasPlanTeams);
	}

	return getTaskListItem({
		variants,
		taskVariants: PLAN.taskVariants,
		taskList: PLAN.taskList,
		taskGroupParent,
	});
};

export const createTailoredOnboardingTasks = (
	taskGroupParent: string,
	teamType?: string,
	features?: {
		hasBacklog?: boolean;
		hasSprint?: boolean;
		hasSummaryPage?: boolean;
		hasRoadmap?: boolean;
		hasList?: boolean;
		hasCalendar?: boolean;
		hasCode?: boolean;
		hasForms?: boolean;
		hasPages?: boolean;
	},
	permissions?: {
		canCreateIssue?: boolean;
	},
): TaskListItem => {
	const variants: TailoredOnboardingVariants[] = [];

	if (features?.hasBacklog) {
		variants.push(Feature.hasBacklog);
	}

	if (features?.hasSprint) {
		variants.push(Feature.hasSprint);
	}

	if (features?.hasSummaryPage) {
		variants.push(Feature.hasSummaryPage);
	}

	if (features?.hasRoadmap) {
		variants.push(Feature.hasRoadmap);
	}

	if (features?.hasList) {
		variants.push(Feature.hasList);
	}

	if (features?.hasCalendar) {
		variants.push(Feature.hasCalendar);
	}

	if (isFeatureEnabled('jira', 'jira-development-tab') && features?.hasCode) {
		variants.push(Feature.hasCode);
	}

	if (features?.hasForms) {
		variants.push(Feature.hasForms);
	}

	if (features?.hasPages) {
		variants.push(Feature.hasPages);
	}

	if (!isFeatureEnabled('jira', 'jira-quickstart-pages')) {
		variants.push(Experience.fedRamp);
	}

	if (permissions?.canCreateIssue) {
		variants.push(Permissions.canCreateIssue);
	}

	if (teamType && isTailoredOnboardingTeamType(teamType)) {
		variants.push(`teamType.${teamType}`);
	} else {
		variants.push('teamType.everythingElse');
	}

	return getTaskListItem({
		variants,
		taskVariants: TAILORED_ONBOARDING_WITH_SINGLE_CONFIG.taskVariants,
		taskList: TAILORED_ONBOARDING_WITH_SINGLE_CONFIG.taskList,
		taskGroupParent,
	});
};

export const createTailoredOnboardingBusinessTasks = ({
	taskGroupParent,
	teamType,
	projectState,
	taskList,
}: {
	taskGroupParent: string;
	teamType: string | undefined;
	projectState?: ProjectState | null;
	taskList?: TaskList;
}): TaskListItem => {
	const variants: TailoredOnboardingVariants[] = [];

	// When cleaning up M2 and consolidating configs, this team type check should just be updated to include MKT & OPS
	if (teamType && isTailoredOnboardingM2P5TailoredTeamType(teamType)) {
		variants.push(`teamType.${teamType}`);
	} else {
		variants.push('teamType.everythingElse');
	}

	if (projectState?.isTrelloCrossflow) {
		variants.push(JWM.experiments.trelloCrossflow);
	}

	if (projectState?.userRole === USER_ROLE.SITE_ADMIN) {
		variants.push(JWM.userRole.siteAdmin);
	}

	if (!isFeatureEnabled('jira', 'jira-quickstart-pages')) {
		variants.push(Experience.fedRamp);
	}

	return getTaskListItem({
		variants,
		taskVariants: TAILORED_ONBOARDING_BUSINESS_CONFIG,
		// This should be cleaned up when M2 is cleaned up. Currently it's like this because M2 populates taskList via the BE
		// and then the FE uses it here, ideally M2 cards are in the business config following conventions so they don't get extra priority bypassing other stuff in the config
		// For now there is also an extra check here to show the trello task list for Trello Crossflow users over M2 https://atlassian.slack.com/archives/C082QR7B16Z/p1755066367960509?thread_ts=1755064885.015779&cid=C082QR7B16Z
		taskList:
			!projectState?.isTrelloCrossflow && taskList
				? taskList
				: TAILORED_ONBOARDING_BUSINESS.taskList,
		taskGroupParent,
	});
};

export const getAllTasks = ({
	projectState,
	taskGroupParent,
	taskList,
	teamType,
	isEligibleForTailoredOnboardingM2P5,
	features,
	containerType,
	permissions,
}: GetTasksProps): TaskListItem => {
	if (containerType === 'plan') {
		return createPlanTasks(taskGroupParent, features);
	}

	if (isJwmProject(projectState)) {
		return createJwmTasks({
			projectState,
			taskGroupParent,
			taskList,
			teamType,
			isEligibleForTailoredOnboardingM2P5,
		});
	}
	if (isJsmProject(projectState)) {
		return createJsmTasks(projectState, taskGroupParent);
	}

	// Implicitly JSW
	const tailoredOnboardingTasks = createTailoredOnboardingTasks(
		taskGroupParent,
		teamType,
		features,
		permissions,
	);

	return tailoredOnboardingTasks;
};
